import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MODEL_LABEL, type ShelfModel } from '@/api/calibrationChart';

// AI197 (#1165): смена нейросети полки/стеллажа — только через предупреждение с кнопкой
// «Продолжить», активной через 3 секунды («после смены модели почти всегда нужно настроить
// пороги по полкам заново»). Один диалог для калибровки и зонирования.

const HOLD_SEC = 3;

export type ModelChoice = ShelfModel | 'inherit';

export default function ModelSwitchDialog({ open, onOpenChange, scope, from, to, shelvesConfigured, busy, onConfirm }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  scope: 'shelf' | 'rack';
  from: ModelChoice;
  to: ModelChoice;
  /** Стеллаж: есть полки со своей моделью — смена стеллажа сбросит их на «как у стеллажа». */
  shelvesConfigured?: boolean;
  busy?: boolean;
  onConfirm: () => void;
}) {
  const [left, setLeft] = useState(HOLD_SEC);
  useEffect(() => {
    if (!open) return;
    setLeft(HOLD_SEC);
    const t = window.setInterval(() => setLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => window.clearInterval(t);
  }, [open]);
  const name = (m: ModelChoice) => (m === 'inherit' ? 'как у стеллажа' : MODEL_LABEL[m]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlert className="size-5 text-amber-500" aria-hidden />
            Сменить нейросеть {scope === 'shelf' ? 'полки' : 'стеллажа'}: {name(from)} → {name(to)}
          </DialogTitle>
          <DialogDescription className="space-y-2 text-sm">
            <span className="block">
                После смены модели почти всегда нужно настроить пороги {scope === 'rack' ? 'по полкам' : 'полки'} заново:
                сырое число у моделей разное (количественная — ширина по боксам, процентная — площадь), поэтому делитель
                и нижняя граница нормы, подобранные под одну модель, для другой не годятся.
            </span>
              {scope === 'rack' && shelvesConfigured && (
                <span className="block font-medium text-amber-700 dark:text-amber-300">
                  Этот стеллаж настроен по полкам: у части полок своя модель. Смена модели стеллажа сбросит их
                  на «как у стеллажа».
                </span>
              )}
              <span className="block text-muted-foreground">
                Старые замеры не пересчитываются: на графике порога они останутся под своей моделью и не смешаются с новыми.
              </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Отмена</Button>
          <Button onClick={onConfirm} disabled={left > 0 || busy}>
            {busy ? 'Сохраняем…' : left > 0 ? `Продолжить (${left})` : 'Продолжить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
